// build-version.ts — which BUILD a telemetry row came from.
//
// The frontend used to report `config.version`, a compile-time constant read out of
// studio/client/packages/frontend/package.json. That file is versioned independently
// of any release, so the string it produced identified the package, never the build:
// a packaged release and an unreleased branch build reported the same value, and no
// row could be attributed to a release.
//
// Two surfaces, two sources of truth, one resolver:
//   • Electron — the main process reads the CI/release stamp
//     (studio/electron/src/main/sideload/version-reader.ts) and forwards it through
//     the `get-env-config` IPC. Only the main process can see the packaged build's
//     identity, so the renderer must be told.
//   • Browser — `__BUILD_VERSION__`, baked by vite.config.js at build time from the
//     release identifier the deploy already knows (git tag, else date+sha+branch).
//
// This module deliberately imports NOTHING: vite.config.js imports `formatBuildVersion`
// at config-evaluation time, where the app's subpath imports (`#~/…`) and browser
// globals do not resolve. Keep it dependency-free.
//
// Per rule: think-in-systems (ai.knowledge/global) — one primitive, three consumers
// (vite.config.js, the analytics context resolver, the PostHog client envelope).
// Per rule: reference-rules-in-comments (ai.knowledge/global).

/**
 * Reported when no build identity is resolvable at all — an un-built test harness,
 * or an Electron renderer whose IPC returned nothing.
 *
 * A real string rather than undefined because `app_version` is a REQUIRED envelope
 * property (analytics/canonical-event-map.json, asserted by
 * studio/electron/e2e/helpers/envelope-contract.ts): dropping it reds the envelope
 * receipts, which is the wrong failure for "this build carries no version".
 */
export const UNKNOWN_BUILD_VERSION = 'unknown';

/** Branch segment substituted for `main`, so no user-facing string ever reads `main`. */
const MAIN_BRANCH_SEGMENT = 'itg';

/** Last-resort branch segment when the checkout is detached and CI named no ref. */
const DETACHED_BRANCH_SEGMENT = 'detached';

/** Short-SHA width, matching `.github/scripts/compute-encoder-version.sh`. */
const SHA_PREFIX_LENGTH = 6;

/** Raw build-time facts the formatter turns into one version string. */
export interface BuildVersionInputs {
  /** Explicit override, e.g. a deploy pipeline that already knows the release. */
  readonly override?: string;
  /** Exact git tag on HEAD, when HEAD is tagged. */
  readonly tag?: string;
  /** UTC build day, `YYYYMMDD`. */
  readonly date?: string;
  /** Commit SHA, full or short — sliced to a stable prefix. */
  readonly sha?: string;
  /** Branch name; `HEAD` and empty are treated as detached. */
  readonly branch?: string;
}

const trimmed = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const result = value.trim();
  return result.length > 0 ? result : undefined;
};

/** Release tags are written `v1.2.3`; every other surface reports the clean form. */
const stripTagPrefix = (tag: string): string => (tag.startsWith('v') ? tag.slice(1) : tag);

/**
 * Format one build-identity string.
 *
 * Mirrors `.github/scripts/compute-encoder-version.sh` on purpose: that script
 * already stamps the encoder and the Electron release, so a row's version string
 * reads the same whichever subsystem produced it, and the two halves of a launch
 * can be compared without a translation table.
 *
 *   1. explicit override           → the override, tag prefix stripped
 *   2. HEAD is tagged              → the tag, prefix stripped (e.g. `1.0.288`)
 *   3. otherwise                   → `{date}-{sha6}-{branch}`, `/` → `-`
 *   4. nothing resolvable          → `unknown`
 *
 * Pure over its inputs so the resolution ORDER is testable without a git tree.
 */
export const formatBuildVersion = (inputs: BuildVersionInputs): string => {
  const override = trimmed(inputs.override);
  if (override !== undefined) {
    return stripTagPrefix(override);
  }

  const tag = trimmed(inputs.tag);
  if (tag !== undefined) {
    return stripTagPrefix(tag);
  }

  const sha = trimmed(inputs.sha);
  const date = trimmed(inputs.date);
  if (sha === undefined || date === undefined) {
    return UNKNOWN_BUILD_VERSION;
  }

  const rawBranch = trimmed(inputs.branch);
  const branch =
    rawBranch === undefined || rawBranch === 'HEAD' ? DETACHED_BRANCH_SEGMENT : rawBranch;
  const branchSegment = branch === 'main' ? MAIN_BRANCH_SEGMENT : branch.replaceAll('/', '-');

  return `${date}-${sha.slice(0, SHA_PREFIX_LENGTH)}-${branchSegment}`;
};

/**
 * Which build this running renderer belongs to.
 *
 * @param electronAppVersion - what the Electron main process resolved, forwarded via
 *   `get-env-config`. Absent on the browser build and on any renderer whose IPC
 *   returned nothing.
 *
 * The Electron value wins because it describes the PACKAGED ARTIFACT, whereas
 * `__BUILD_VERSION__` describes only the renderer bundle inside it — and because
 * matching it is what makes renderer and main rows from one launch name the same
 * build.
 */
export const resolveBuildVersion = (electronAppVersion?: string): string => {
  const forwarded = trimmed(electronAppVersion);
  if (forwarded !== undefined) {
    return forwarded;
  }

  // typeof-guarded because the constant only exists in a vite-built bundle; unit
  // tests and any non-vite consumer see it undefined.
  const baked = typeof __BUILD_VERSION__ !== 'undefined' ? trimmed(__BUILD_VERSION__) : undefined;
  return baked ?? UNKNOWN_BUILD_VERSION;
};
